import { Database } from '@/integrations/supabase/types';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/uploadFile';
import { getProxiedImageUrl } from '@/services/imageProxyService';
import { PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { ClothingItem, ClothingItemFormData } from '@/models/types';

export interface FilterOptions {
  category?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  searchTerm?: string;
  itemId?: number;
  sortOption?: string;
}

export const getClothingItems = async (page = 1, perPage = 10, filters: FilterOptions = {}): Promise<{ data: ClothingItem[]; count: number }> => {
  let query = supabase
    .from('db')
    .select('*', { count: 'exact' });

  // Apply filters
  if (filters.category && filters.category !== '_all') {
    query = query.or(`"category[0]".eq."${filters.category}","category[1]".eq."${filters.category}","category[2]".eq."${filters.category}"`);
  }
  
  if (filters.brand && filters.brand !== '_all') {
    query = query.eq('brand', filters.brand);
  }
  
  if (filters.minPrice !== undefined && !isNaN(filters.minPrice)) {
    query = query.gte('price', filters.minPrice);
  }
  
  if (filters.maxPrice !== undefined && !isNaN(filters.maxPrice)) {
    query = query.lte('price', filters.maxPrice);
  }
  
  if (filters.itemId !== undefined && !isNaN(filters.itemId)) {
    query = query.eq('_id', filters.itemId);
  }
  
  if (filters.searchTerm) {
    query = query.or(`name.ilike.%${filters.searchTerm}%,brand.ilike.%${filters.searchTerm}%,"category[0]".ilike.%${filters.searchTerm}%,"category[1]".ilike.%${filters.searchTerm}%,"category[2]".ilike.%${filters.searchTerm}%`);
  }

  // Apply sorting
  if (filters.sortOption) {
    const [field, direction] = filters.sortOption.split('_');
    if (field === 'id') {
      query = query.order('_id', { ascending: direction === 'asc' });
    } else {
      query = query.order(field, { ascending: direction === 'asc' });
    }
  } else {
    query = query.order('created_at', { ascending: false });
  }

  // Apply pagination
  const start = (page - 1) * perPage;
  query = query.range(start, start + perPage - 1);

  const { data, error, count } = await query;

  if (error) throw error;
  return { 
    data: (data || []) as unknown as ClothingItem[], 
    count: count || 0 
  };
};

export const getClothingItem = async (id: number): Promise<ClothingItem> => {
  const { data, error } = await supabase
    .from('db')
    .select('*')
    .eq('_id', id)
    .single();

  if (error) throw error;
  return data as unknown as ClothingItem;
};

export const createClothingItem = async (formData: ClothingItemFormData): Promise<ClothingItem> => {
  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Proxy the image URL if it's a string
    imageUrl = await getProxiedImageUrl(formData.image);
  }

  const { data: { user } } = await supabase.auth.getUser();
  const timestamp = new Date().toISOString();

  // Find the highest existing ID to ensure sequential IDs
  const { data: maxIdData, error: maxIdError } = await supabase
    .from('db')
    .select('_id')
    .order('_id', { ascending: false })
    .limit(1);

  if (maxIdError) throw maxIdError;
  
  // Use the next available ID (highest + 1), or start at 1 if no items exist
  const nextId = maxIdData && maxIdData.length > 0 ? (maxIdData[0]._id as number) + 1 : 1;

  const insertData = {
    _id: nextId,
    name: formData.name,
    brand: formData.brand,
    price: formData.price,
    image: imageUrl,
    link: formData.link,
    "category[0]": formData["category[0]"],
    "category[1]": formData["category[1]"],
    "category[2]": formData["category[2]"],
    batch: formData.batch,
    created_by: user?.email || 'Unknown user',
    created_at: timestamp,
    updated_at: timestamp
  } as any;

  const { data, error } = await supabase
    .from('db')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as ClothingItem;
};

export const updateClothingItem = async (id: number, formData: ClothingItemFormData): Promise<ClothingItem> => {
  let imageUrl = '';
  
  if (formData.image instanceof File) {
    try {
      imageUrl = await uploadFile(formData.image);
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  } else if (typeof formData.image === 'string') {
    // Only proxy external images that aren't already from supabase
    if (!formData.image.includes('supabase.co')) {
      imageUrl = await getProxiedImageUrl(formData.image);
    } else {
      imageUrl = formData.image;
    }
  }

  const { data: { user } } = await supabase.auth.getUser();
  const timestamp = new Date().toISOString();

  const updateData = {
    name: formData.name,
    brand: formData.brand,
    price: formData.price,
    image: imageUrl,
    link: formData.link,
    "category[0]": formData["category[0]"],
    "category[1]": formData["category[1]"],
    "category[2]": formData["category[2]"],
    batch: formData.batch,
    updated_at: timestamp
  } as any;

  const { data, error } = await supabase
    .from('db')
    .update(updateData)
    .eq('_id', id)
    .select()
    .single();

  if (error) throw error;
  return data as unknown as ClothingItem;
};

export const deleteClothingItem = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('db')
    .delete()
    .eq('_id', id);

  if (error) throw error;
};
